import { createFileRoute } from '@tanstack/react-router';
import {
  EvaluationTypeLabelsEnum,
  queryKeys,
  DocumentTypes,
  ContractSupplementPackageDocFilters,
  SummarizationNamespaces,
} from '@/app/utils/constants';
import { useTranslations } from 'use-intl';
import { useMemo, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeftRightIcon, FolderIcon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { deleteSupplementDocuments } from '@/app/services/supplementsService';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
} from '@/app/ui/components/breadcrumb';
import {
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderSidechatButton,
  Header,
  HeaderButton,
} from '@/app/ui/components/header';
import { MainScrollArea } from '@/app/ui/components/main';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import { FilesList } from '@/app/components/shared/EntityDocuments/FilesList';
import { FileUploadDialog } from '@/app/components/shared/EntityDocuments/FileUploadDialog/FileUploadDialog';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/contract-management/$supplementId/_layout/(supplement)/_layout/files',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.contractManagement');
  const tHome = useTranslations('pages.contractHome');
  const tFiles = useTranslations('pages.files');
  const tFilters = useTranslations('components.contractSupplements.filters');
  const { contractId, projectId, supplementId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const [open, setOpen] = useState(false);
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));

  const filterOptions = useMemo(
    () => [
      {
        label: tFilters('meetingNote'),
        value: ContractSupplementPackageDocFilters.MEETING_MINUTES,
      },
      {
        label: tFilters('invoice'),
        value: ContractSupplementPackageDocFilters.INVOICES,
      },
      {
        label: tFilters('changeRecord'),
        value: ContractSupplementPackageDocFilters.CHANGE_ORDERS,
      },
      {
        label: tFilters('other'),
        value: ContractSupplementPackageDocFilters.OTHER_DOCUMENTS,
      },
    ],
    [tFilters],
  );

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId/contract-management/$supplementId"
                params={{ projectId, contractId, supplementId }}
              >
                <HugeiconsIcon icon={ArrowLeftRightIcon} />
                {t('titleLong')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={FolderIcon} />
                {tHome('nav.files')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderButton aria-label={tFiles('addFiles')} onClick={() => setOpen(true)}>
          <HugeiconsIcon icon={PlusSignIcon} />
        </HeaderButton>
      </Header>
      <MainScrollArea>
        <DocumentAlerts
          resourceId={supplementId}
          summaryType={SummarizationNamespaces.SUPPLEMENT}
        />
        <FilesList
          resourceId={supplementId}
          resourceType={DocumentTypes.SUPPLEMENT}
          onAddClickAction={() => setOpen(true)}
          deleteDocumentsAction={deleteSupplementDocuments}
          detailsQueryKey={[queryKeys.SUPPLEMENT_INDEXED_DOCUMENTS_STATUS, supplementId]}
          filterOptions={filterOptions}
        />
      </MainScrollArea>
      <FileUploadDialog
        resourceId={supplementId}
        resourceType={DocumentTypes.SUPPLEMENT}
        open={open}
        onOpenChange={setOpen}
        detailsQueryKey={[queryKeys.SUPPLEMENT_INDEXED_DOCUMENTS_STATUS, supplementId]}
      />
    </>
  );
}
